import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowDown } from "lucide-react";
import { SplitReveal } from "../components/SplitReveal";
import { useLanguage, waLink } from "../lib/i18n";
import { usePrefersReducedMotion } from "../lib/useReducedMotion";
import { IMAGES } from "../lib/images";

gsap.registerPlugin(ScrollTrigger);

// HERO — "Kaplama Sahnesi": protection film sweeps down over the car on scroll.
export function Hero() {
  const { t } = useLanguage();
  const reduced = usePrefersReducedMotion();
  const wrapRef = useRef(null);
  const filmRef = useRef(null);
  const lineRef = useRef(null);
  const carRef = useRef(null);

  useEffect(() => {
    if (reduced || !wrapRef.current) return;
    const state = { p: 0 };
    const apply = () => {
      const p = state.p;
      if (filmRef.current) filmRef.current.style.clipPath = `inset(0 0 ${(1 - p) * 100}% 0)`;
      if (lineRef.current) {
        lineRef.current.style.top = `${p * 100}%`;
        lineRef.current.style.opacity = p > 0.01 && p < 0.99 ? "1" : "0";
      }
      if (carRef.current) carRef.current.style.filter = `brightness(${0.85 + p * 0.4}) contrast(${1 + p * 0.15})`;
    };
    apply();
    const tween = gsap.to(state, {
      p: 1,
      ease: "none",
      onUpdate: apply,
      scrollTrigger: {
        trigger: wrapRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.6,
      },
    });
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [reduced]);

  const msg = "Merhaba HARSPRO, ücretsiz keşif randevusu almak istiyorum.";

  return (
    <section ref={wrapRef} id="top" className="relative h-[220vh]" data-testid="hero-section">
      <div className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden">
        {/* Ambient background glow */}
        <div className="absolute inset-0 bg-[#08090B]" />
        <div className="absolute left-1/2 top-1/2 h-[70vh] w-[70vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-hars-orange/10 blur-[120px]" />

        {/* Car — base + film layer (glossier) */}
        <div className="absolute inset-0">
          <img
            ref={carRef}
            src={IMAGES.heroSuv}
            alt="HARSPRO ile korunan lüks SUV"
            className="h-full w-full object-cover md:object-contain md:object-center"
            style={{ filter: "brightness(0.85)" }}
            loading="eager"
          />
          {/* Protection film sweep layer */}
          <div
            ref={filmRef}
            className="absolute inset-0"
            style={{ clipPath: reduced ? "none" : "inset(0 0 100% 0)" }}
          >
            <img
              src={IMAGES.heroSuv}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover md:object-contain md:object-center"
              style={{ filter: "brightness(1.25) contrast(1.15) saturate(1.1)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent mix-blend-overlay" />
          </div>
          {/* Sweep leading edge (glowing orange line) */}
          <div
            ref={lineRef}
            className="pointer-events-none absolute left-0 h-[2px] w-full bg-hars-orangeLight opacity-0 shadow-[0_0_30px_8px_rgba(240,116,42,0.7)]"
            style={{ top: "0%" }}
            aria-hidden="true"
          />
        </div>

        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#08090B] via-[#08090B]/60 to-transparent" />

        {/* Copy */}
        <div className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-col px-6 md:px-12">
          <div className="mt-24 max-w-4xl">
            <div className="mb-6 inline-block rounded-full border border-hars-orange/40 bg-hars-orange/10 px-4 py-1.5 font-inter text-xs font-semibold tracking-[0.2em] text-hars-orangeLight">
              {t.hero.badge}
            </div>
            <SplitReveal
              as="h1"
              text={t.hero.title}
              className="heading text-[13vw] text-white md:text-[8.5vw] lg:text-[7vw]"
              delay={0.1}
              stagger={0.12}
            />
            <p className="mt-6 max-w-xl font-inter text-base text-hars-muted md:text-lg">
              {t.hero.sub}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="#contact"
                data-cursor
                data-cursor-label="KEŞİF"
                data-testid="hero-cta-primary"
                className="rounded-full bg-hars-orange px-7 py-3.5 font-inter text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
              >
                {t.hero.cta1}
              </a>
              <a
                href={waLink(msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-cursor
                data-testid="hero-cta-whatsapp"
                className="rounded-full border border-white/20 px-7 py-3.5 font-inter text-sm font-semibold text-white transition-colors duration-300 hover:border-hars-orange hover:text-hars-orange"
              >
                {t.hero.cta2}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 z-10 flex flex-col items-center gap-2 text-hars-muted">
          <span className="heading text-[10px] tracking-widest">{t.hero.scroll}</span>
          <ArrowDown size={16} className="animate-bounce text-hars-orange" />
        </div>
      </div>
    </section>
  );
}
